import Fuse from 'fuse.js'
import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '../../components/Box.tsx'
import { Alert, Autocomplete, Paper, TextField, Tooltip, Typography } from '../../components/generic.tsx'
import { ids } from '../../ids.ts'
import { findTimezone, getTimezoneOffset, type Timezone, timezones, UTC_TIMEZONE } from './timezones.ts'
import { useFormatDate } from './useFormatDate.tsx'

export const TimezonePicker = ({ value, onChange, dataTa }: { value: string; onChange: (value: string) => void; dataTa?: string }) => {
	const { t } = useTranslation()
	const [timezoneSearch, setTimezoneSearch] = useState('')
	const [open, setOpen] = useState(false)
	const rootRef = useRef<HTMLDivElement>(null)
	const [placement, setPlacement] = useState<'bottom-start' | 'top-start'>('bottom-start')

	const filteredTimezones = useMemo(() => timezoneFilterOptions(timezoneSearch), [timezoneSearch])
	// If the search matched exactly one zone through a non-primary city (e.g. typing "Espoo" matches
	// the "Helsinki / Finland" zone), call that out - otherwise the result looks unrelated to the search.
	const rawTimezoneSameAs = useMemo(() => {
		const query = normalize(timezoneSearch.trim().toLowerCase())
		const [match, ...rest] = filteredTimezones
		if (!query || !match || rest.length > 0 || normalize(match.mainCity.toLowerCase()).includes(query)) return undefined
		const city = match.mainCities.slice(1).find((c) => normalize(c.toLowerCase()).includes(query))
		return city ? { city, mainCity: match.mainCity } : undefined
	}, [filteredTimezones, timezoneSearch])
	const timezoneSameAs = useStable(rawTimezoneSameAs, (a, b) => a?.city === b?.city && a?.mainCity === b?.mainCity)

	// Falls back to UTC if value is unset/unknown - the field is required and non-clearable, so it
	// must never resolve to no option.
	const selected = findTimezone(value) ?? UTC_TIMEZONE

	// Memoized so the slot keeps a stable component identity across renders (e.g. every keystroke) -
	// MUI treats a new slot component reference as a new type and remounts it, which discards the
	// listbox's scroll position and can drop in-flight clicks on options.
	const PaperSlot = useCallback(
		({ children, ...paperProps }: ComponentProps<typeof Paper>) => (
			<Paper data-ta={ids.preferences.regionalPreferences.timezone.listboxPopup} {...paperProps}>
				{children}
				{timezoneSameAs && (
					<Alert severity='caution' sx={{ borderRadius: 0 }}>
						<Typography variant='label'>
							{t(($) => $.preferences.timezoneSameAs, { city: timezoneSameAs.city, mainCity: timezoneSameAs.mainCity })}
						</Typography>
					</Alert>
				)}
			</Paper>
		),
		[timezoneSameAs, t],
	)

	// Keep the (controlled) input text showing the selection while closed - ignoring MUI's 'reset'-reason
	// resyncs in onInputChange (needed so opening never shows a list pre-filtered by the prior label) also
	// means nothing else sets this text on mount or on an external value change (e.g. autodetect), so do
	// it explicitly here instead of relying on MUI's own sync.
	useEffect(() => {
		if (!open) setTimezoneSearch(selected.label)
	}, [open, selected])

	return (
		<Autocomplete
			ref={rootRef}
			data-ta={dataTa}
			fullWidth
			open={open}
			onOpen={() => {
				// Decide placement once, up-front (using the worst-case 40vh listbox height), then lock it -
				// otherwise the popper's flip modifier re-measures on every keystroke as the option count
				// (and so the listbox height) changes, flipping the list between above/below while typing.
				const rect = rootRef.current?.getBoundingClientRect()
				if (rect) {
					const maxListboxHeight = window.innerHeight * 0.4
					const spaceBelow = window.innerHeight - rect.bottom
					const spaceAbove = rect.top
					setPlacement(spaceBelow < maxListboxHeight && spaceAbove > spaceBelow ? 'top-start' : 'bottom-start')
				}
				setTimezoneSearch('')
				setOpen(true)
			}}
			onClose={() => setOpen(false)}
			slotProps={{ popper: { placement, modifiers: [{ name: 'flip', enabled: false }] } }}
			value={selected}
			onChange={(_, option) => option && onChange(option.value)}
			inputValue={timezoneSearch}
			onInputChange={(_, newInputValue, reason) => {
				// MUI resyncs the (controlled) input text to the selected option's label on focus/blur/
				// selection via a 'reset'-reason call - ignore those so a focus-triggered resync can't
				// stomp the empty search onOpen just set (only real typing/clearing should update it).
				if (reason === 'reset') return
				setTimezoneSearch(newInputValue)
			}}
			options={filteredTimezones}
			getOptionLabel={(tz) => tz.label}
			isOptionEqualToValue={(tz, value) => tz.value === value.value}
			filterOptions={(options) => options}
			slots={{ paper: PaperSlot }}
			renderOption={({ key, ...optionProps }, tz) => (
				<Box component='li' key={key} {...optionProps}>
					<TimezoneRow tz={tz} />
				</Box>
			)}
			disableClearable
			renderInput={(params) => (
				<Box sx={{ position: 'relative' }}>
					<TextField {...params} sx={{ '& .MuiInputBase-input': { color: open ? undefined : 'transparent' } }} />
					{!open && selected && (
						<Box flex align='center' sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', pl: '14px', pr: 5, overflow: 'hidden' }}>
							<TimezoneRow tz={selected} />
						</Box>
					)}
				</Box>
			)}
		/>
	)
}

const useStable = <T,>(value: T, isSame: (a: T, b: T) => boolean): T => {
	const ref = useRef(value)
	if (!isSame(ref.current, value)) ref.current = value
	return ref.current
}

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '')

// Diacritic-insensitive lookup ("sao paulo" should match "São Paulo") - Fuse doesn't normalize
// accents itself, so read the (always top-level) field directly and normalize it.
const normalizingGetFn = (tz: Timezone, path: string | string[]) => {
	const value = tz[(Array.isArray(path) ? path[0] : path) as keyof Timezone]
	return Array.isArray(value) ? value.map((v) => normalize(v.toLowerCase())) : normalize(String(value).toLowerCase())
}

// Keys to search, weighted from most to least specific - lets a match on e.g. the country name (typing
// "china") still rank below a match on the city itself, and keeps incidental substring hits (typing
// "china" matching "Chinandega" or "Indochina") from outranking the zones that are actually about China.
const timezoneFuse = new Fuse(timezones, {
	keys: [
		{ name: 'label', weight: 6 },
		{ name: 'mainCity', weight: 5 },
		{ name: 'countryName', weight: 4 },
		{ name: 'mainCities', weight: 3 },
		{ name: 'alternativeName', weight: 2 },
		{ name: 'continentName', weight: 1 },
	],
	threshold: 0,
	ignoreLocation: true,
	getFn: normalizingGetFn,
})

const timezoneFilterOptions = (inputValue: string) => {
	const query = normalize(inputValue.trim().toLowerCase())
	if (!query) return timezones
	return timezoneFuse.search(query).map((result) => result.item)
}

const TimezoneRow = ({ tz }: { tz: Timezone }) => {
	const formatDate = useFormatDate()
	return (
		<Box flex align='center' gap={2} sx={{ width: '100%' }}>
			<Tooltip
				title={tz.label}
				slotProps={{
					// Popper's 'hide' modifier flags (via data-popper-reference-hidden) when the anchor row is
					// scrolled out of the listbox's clipping bounds - without this the tooltip (a portal) keeps
					// floating in place instead of following the row out of view.
					popper: {
						modifiers: [{ name: 'hide', enabled: true }],
						sx: { '&[data-popper-reference-hidden]': { visibility: 'hidden', pointerEvents: 'none' } },
					},
				}}
			>
				<Typography variant='body2' noWrap sx={{ minWidth: 0 }}>
					{tz.label}
				</Typography>
			</Tooltip>
			<Typography variant='label' sx={{ color: 'text.secondary', whiteSpace: 'nowrap', ml: 'auto', pl: 2 }}>
				{`UTC${getTimezoneOffset(tz.value)}, ${formatDate(new Date(), 'DD.MM.HH.mm', tz.value)}`}
			</Typography>
		</Box>
	)
}
